import { useEffect, useRef, useState } from 'react';
import type { ReactNode, RefObject } from 'react';
import { ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth/AuthContext';
import { Modal } from './Modal';
import { Pagination } from './Pagination';
import {
  getAdminUserDetail,
  getAdminUserRuns,
  flagUserRun,
  unflagUserRun,
  type UserDetail,
  type UserDetailWeekScore,
  type UserRun,
  type UserRunCheatSignals,
  type UserStatusValue,
  type UserTypeValue,
} from '../../lib/api/admin';
import { fmtDate, fmtDateTime, fmtNum } from '../../lib/format';
import { countryName } from '../../lib/countries';

/**
 * Users tab sub-view — full picture of one player: profile, contact + consent
 * state, coin bank, owned stats, per-week scores (incl. DQ + run count),
 * per-run history with anti-cheat telemetry, rules consents, and recent audit
 * activity touching this user. Reads only EXCEPT run flagging — an admin can
 * flag/unflag an individual run for review from the run-history card here
 * (an internal marker; it does not touch the leaderboard). All account-level
 * moderation stays on the list view.
 */
export function UserDetailView({ userId, onBack }: { userId: string; onBack: () => void }) {
  const [detail, setDetail] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    getAdminUserDetail(userId, { signal: controller.signal })
      .then((d) => {
        setDetail(d);
        setError(null);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        if (err instanceof ApiError && err.status === 404) {
          setError('User not found.');
        } else {
          setError(err instanceof ApiError ? `Couldn't load user (${err.status}).` : "Couldn't load user.");
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [userId]);

  return (
    <div className="flex flex-col gap-5">
      <button
        type="button"
        onClick={onBack}
        className="self-start text-sm text-white/70 transition hover:text-white"
      >
        &larr; Back to users
      </button>

      {loading && !detail && <p className="text-white/55">Loading user…</p>}
      {error && <p className="text-[#ff8da6]">{error}</p>}

      {detail && (
        <>
          <ProfileCard detail={detail} />
          <div className="grid gap-4 lg:grid-cols-2">
            <BankCard detail={detail} />
            <OwnedStatsCard detail={detail} />
          </div>
          <WeekScoresCard scores={detail.weekScores} />
          <RunHistoryCard userId={detail.profile.userId} />
          <div className="grid gap-4 lg:grid-cols-2">
            <ConsentsCard detail={detail} />
            <AuditTailCard detail={detail} />
          </div>
        </>
      )}
    </div>
  );
}

function ProfileCard({ detail }: { detail: UserDetail }) {
  const p = detail.profile;
  return (
    <Card>
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h2 className="font-display text-xl uppercase tracking-wide text-white">
            {p.displayName || `${p.firstName} ${p.lastName}`.trim() || p.email}
          </h2>
          <p className="text-sm text-white/55 [overflow-wrap:anywhere]">{p.email}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <StatusBadge status={p.status} />
          <TypeBadge type={p.type} />
          {!p.enabled && <PillBadge tone="danger">Disabled</PillBadge>}
          {!p.canEmail && (
            <PillBadge
              tone="warn"
              title={
                [p.canEmailReason, p.canEmailAt ? `since ${fmtDate(p.canEmailAt)}` : null]
                  .filter(Boolean)
                  .join(', ') || undefined
              }
            >
              Email undeliverable
            </PillBadge>
          )}
        </div>
      </header>

      <dl className="mt-4 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
        <Field label="Full name" value={`${p.firstName} ${p.lastName}`.trim() || '—'} />
        <Field label="Country" value={p.country ? `${countryName(p.country)} (${p.country})` : '—'} />
        <Field label="Phone" value={p.phone || '—'} />
        <Field label="Joined" value={fmtDate(p.createdAt)} />
        <Field label="Last login" value={fmtDateTime(p.lastLoginAt)} />
        <Field label="Age confirmed" value={p.ageConfirmedAt ? fmtDateTime(p.ageConfirmedAt) : 'Not confirmed'} />
        <Field
          label="Marketing consent"
          value={
            p.marketingConsent == null
              ? 'Not set'
              : p.marketingConsent
                ? `Yes (${fmtDate(p.marketingConsentAt)})`
                : 'No'
          }
        />
        <Field
          label="Episode reminders"
          value={p.receivesEpisodeReminders ? 'Subscribed' : `Unsubscribed (${fmtDate(p.receivesEpisodeRemindersAt)})`}
        />
        <Field
          label="Admin promotable"
          value={p.promotable ? 'Yes (@infinitymarketing.com)' : 'No — domain not allowed'}
        />
        <Field
          label="Email deliverability"
          value={
            p.canEmail
              ? 'OK'
              : `Undeliverable — ${p.canEmailReason ?? 'bounce/complaint'}${
                  p.canEmailAt ? ` (${fmtDate(p.canEmailAt)})` : ''
                }`
          }
        />
      </dl>

      {!p.enabled && (
        <div className="mt-4 rounded-md border border-[#c0344f]/40 bg-[#3a0e1a]/40 p-3 text-sm text-[#ffb3c4]">
          <strong className="font-semibold text-[#ff8da6]">Disabled</strong>
          {p.disabledReason ? `: ${p.disabledReason}` : ''}
          {p.disabledByEmail || p.disabledAt ? (
            <span className="text-white/60">
              {' '}
              ({p.disabledByEmail ? `by ${p.disabledByEmail}` : 'by unknown'}
              {p.disabledAt ? ` on ${fmtDateTime(p.disabledAt)}` : ''})
            </span>
          ) : null}
        </div>
      )}
    </Card>
  );
}

function BankCard({ detail }: { detail: UserDetail }) {
  const b = detail.bank;
  return (
    <Card title="Coin bank">
      <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
        <Field label="Balance" value={fmtNum(b.balance)} />
        <Field label="Lifetime earned" value={fmtNum(b.lifetimeEarned)} />
        <Field label="Lifetime spent" value={fmtNum(b.lifetimeSpent)} />
      </dl>
    </Card>
  );
}

function OwnedStatsCard({ detail }: { detail: UserDetail }) {
  return (
    <Card title={`Owned stats (${detail.ownedStats.length})`}>
      {detail.ownedStats.length === 0 ? (
        <p className="text-sm text-white/50">No stats purchased yet.</p>
      ) : (
        <ul className="flex flex-col gap-1.5 text-sm">
          {detail.ownedStats.map((s) => (
            <li key={s.statId} className="flex items-baseline justify-between gap-3">
              <span className="text-white/85">{s.name}</span>
              <span className="text-xs text-white/55">
                Lv {s.currentLevel}/{s.maxLevel}
                {s.lastUpgradedAt ? ` · ${fmtDate(s.lastUpgradedAt)}` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function WeekScoresCard({ scores }: { scores: UserDetailWeekScore[] }) {
  return (
    <Card title="Weekly play">
      {scores.length === 0 ? (
        <p className="text-sm text-white/50">No runs recorded.</p>
      ) : (
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-white/10 text-white/55">
              <Th>Week</Th>
              <Th>High score</Th>
              <Th>Achieved</Th>
              <Th>Runs</Th>
              <Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {scores.map((w) => (
              <tr key={w.weekNumber} className="border-b border-white/5 align-top last:border-0">
                <td className="px-3 py-2 text-white">Week {w.weekNumber}</td>
                <td className="px-3 py-2 text-white/85">{fmtNum(w.highScore)}</td>
                <td className="px-3 py-2 text-white/60">{w.achievedAt ? fmtDateTime(w.achievedAt) : '—'}</td>
                <td className="px-3 py-2 text-white/70">{w.runs}</td>
                <td className="px-3 py-2">
                  {w.disqualified ? (
                    <span className="text-[#ff8da6]">
                      DQ
                      {w.disqualifyReason ? `: ${w.disqualifyReason}` : ''}
                      {w.disqualifiedByEmail ? ` (by ${w.disqualifiedByEmail})` : ''}
                    </span>
                  ) : (
                    <span className="text-[#5bffb4]">Eligible</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

// ── Run history + telemetry inspector ──────────────────────────────────

// True pagination (not "load more") so a heavy player's history can't grow the
// DOM unbounded — only the current page is ever rendered.
const RUN_PAGE_SIZE_OPTIONS = [20, 50];
const DEFAULT_RUN_PAGE_SIZE = 20;

/**
 * Per-run inspector. Lists the player's individual runs (score_records) with
 * their breakdown metrics and the collect-only anti-cheat telemetry. Runs that
 * carry cheat signals are auto-highlighted. An admin can flag a run for review
 * (internal marker + note; does NOT touch the leaderboard — use Disqualify on
 * the Leaderboard tab for that).
 */
function RunHistoryCard({ userId }: { userId: string }) {
  const auth = useAuth();
  const [runs, setRuns] = useState<UserRun[]>([]);
  const [total, setTotal] = useState(0);
  const [weeks, setWeeks] = useState<number[]>([]);
  const [weekFilter, setWeekFilter] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_RUN_PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [flagTarget, setFlagTarget] = useState<UserRun | null>(null);
  const [unflaggingId, setUnflaggingId] = useState<number | null>(null);

  // One page at a time — refetches on page / page-size / week-filter change.
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setExpandedId(null);
    getAdminUserRuns(
      userId,
      { week: weekFilter ?? undefined, limit: pageSize, offset: (page - 1) * pageSize },
      { signal: controller.signal },
    )
      .then((d) => {
        setRuns(d.runs);
        setTotal(d.total);
        setWeeks(d.weeks); // filter-independent (all weeks the user has runs in)
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err instanceof ApiError ? `Couldn't load runs (${err.status}).` : "Couldn't load runs.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [userId, weekFilter, page, pageSize]);

  // Clamp the page if the result set shrinks (e.g. a tighter week filter).
  useEffect(() => {
    if (total === 0) return;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    setPage((cur) => (cur > totalPages ? totalPages : cur));
  }, [total, pageSize]);

  const selectWeek = (w: number | null): void => {
    setWeekFilter(w);
    setPage(1);
  };

  const patchRun = (recordId: number, patch: Partial<UserRun>): void => {
    setRuns((cur) => cur.map((r) => (r.recordId === recordId ? { ...r, ...patch } : r)));
  };

  const onUnflag = async (run: UserRun): Promise<void> => {
    setUnflaggingId(run.recordId);
    setError(null);
    try {
      await unflagUserRun(userId, run.recordId);
      patchRun(run.recordId, { flaggedAt: null, flaggedReason: null, flaggedByEmail: null });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not unflag that run.');
    } finally {
      setUnflaggingId(null);
    }
  };

  return (
    <Card title="Run history">
      <p className="-mt-1 mb-3 text-xs leading-relaxed text-white/45">
        Each run with its breakdown and anti-cheat telemetry. Runs with{' '}
        <span className="text-[#ff8da6]">cheat signals</span> are highlighted. Flagging marks a run
        for review and is recorded in the audit log; it does <span className="text-white/70">not</span>{' '}
        change the leaderboard (use Disqualify on the Leaderboard tab for that).
      </p>

      {weeks.length > 1 && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-wide text-white/45">Week</span>
          <WeekChip label="All" active={weekFilter == null} onClick={() => selectWeek(null)} />
          {weeks.map((w) => (
            <WeekChip
              key={w}
              label={`Week ${w}`}
              active={weekFilter === w}
              onClick={() => selectWeek(w)}
            />
          ))}
        </div>
      )}

      {error && <p className="mb-3 text-sm text-[#ff8da6]">{error}</p>}

      {loading && runs.length === 0 ? (
        <p className="text-sm text-white/50">Loading runs…</p>
      ) : runs.length === 0 ? (
        <p className="text-sm text-white/50">
          {weekFilter == null ? 'No runs recorded.' : `No runs recorded for Week ${weekFilter}.`}
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          <div
            aria-busy={loading}
            className={`overflow-x-auto transition-opacity ${loading ? 'opacity-60' : 'opacity-100'}`}
          >
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-white/10 text-white/55">
                  <Th>Week</Th>
                  <Th>When</Th>
                  <Th>Score</Th>
                  <Th>Metrics</Th>
                  <Th>Signals</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => {
                  const expanded = expandedId === r.recordId;
                  return (
                    <RunRow
                      key={r.recordId}
                      run={r}
                      expanded={expanded}
                      onToggle={() => setExpandedId(expanded ? null : r.recordId)}
                      onFlag={() => setFlagTarget(r)}
                      onUnflag={() => void onUnflag(r)}
                      unflagging={unflaggingId === r.recordId}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>

          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            pageSizeOptions={RUN_PAGE_SIZE_OPTIONS}
            disabled={loading}
            itemLabel="runs"
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        </div>
      )}

      {flagTarget && (
        <FlagRunModal
          userId={userId}
          run={flagTarget}
          onClose={() => setFlagTarget(null)}
          onDone={(reason) => {
            patchRun(flagTarget.recordId, {
              flaggedAt: new Date().toISOString(),
              flaggedReason: reason,
              flaggedByEmail: auth.user?.email ?? null,
            });
            setFlagTarget(null);
          }}
        />
      )}
    </Card>
  );
}

function RunRow({
  run,
  expanded,
  onToggle,
  onFlag,
  onUnflag,
  unflagging,
}: {
  run: UserRun;
  expanded: boolean;
  onToggle: () => void;
  onFlag: () => void;
  onUnflag: () => void;
  unflagging: boolean;
}) {
  const suspicious = run.suspicious;
  return (
    <>
      <tr
        className={`border-b align-top text-white/85 ${
          suspicious ? 'border-[#c0344f]/25 bg-[#c0344f]/[0.07]' : 'border-white/5'
        } ${expanded ? '' : 'last:border-0'}`}
      >
        <td className="px-3 py-2">
          <span className="text-white">Week {run.weekNumber}</span>
          {run.isWeekHighScore && (
            <span
              title="This run is the player's high score for the week"
              className="ml-1.5 rounded bg-[#0f3d2a] px-1.5 py-0.5 text-[10px] text-[#5bffb4]"
            >
              ★ High
            </span>
          )}
        </td>
        <td className="px-3 py-2 text-white/60">{fmtDateTime(run.createdAt)}</td>
        <td className="px-3 py-2 tabular-nums text-white/90">{fmtNum(run.score)}</td>
        <td className="px-3 py-2 text-xs text-white/60">
          <RunMetricsSummary run={run} />
        </td>
        <td className="px-3 py-2">
          <RunSignals run={run} />
        </td>
        <td className="px-3 py-2">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onToggle}
              aria-expanded={expanded}
              className="rounded-full border border-white/25 px-3 py-1 text-xs text-white/80 transition hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              {expanded ? 'Hide' : 'Details'}
            </button>
            {run.flaggedAt ? (
              <button
                type="button"
                disabled={unflagging}
                onClick={onUnflag}
                className="rounded-full border border-[#00ff8b]/40 px-3 py-1 text-xs text-[#5bffb4] transition hover:bg-[#00ff8b]/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {unflagging ? 'Working…' : 'Unflag'}
              </button>
            ) : (
              <button
                type="button"
                onClick={onFlag}
                className="rounded-full border border-[#c0344f]/50 px-3 py-1 text-xs text-[#ff8da6] transition hover:bg-[#c0344f]/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                Flag
              </button>
            )}
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className={`border-b ${suspicious ? 'border-[#c0344f]/25' : 'border-white/5'} last:border-0`}>
          <td colSpan={6} className={`px-3 pb-4 pt-1 ${suspicious ? 'bg-[#c0344f]/[0.04]' : ''}`}>
            <RunDetail run={run} />
          </td>
        </tr>
      )}
    </>
  );
}

function RunMetricsSummary({ run }: { run: UserRun }) {
  const b = run.breakdown;
  const parts: string[] = [];
  if (b.enemiesDefeated != null) parts.push(`${fmtNum(b.enemiesDefeated)} enemies`);
  if (b.jewelsCollected != null) parts.push(`${fmtNum(b.jewelsCollected)} jewels`);
  if (b.levelReached != null) parts.push(`Lv ${b.levelReached}`);
  if (b.survivalSeconds != null) parts.push(fmtDuration(b.survivalSeconds));
  if (b.bossDefeated) parts.push('Boss ✓');
  return parts.length === 0 ? <span className="text-white/35">—</span> : <span>{parts.join(' · ')}</span>;
}

function RunSignals({ run }: { run: UserRun }) {
  const active = activeSignalLabels(run.cheatSignals);
  return (
    <div className="flex flex-wrap gap-1">
      {run.suspicious && (
        <span
          title={active.join(', ') || 'Cheat signals present'}
          className="rounded bg-[#3a0e1a] px-2 py-0.5 text-[11px] text-[#ff8da6]"
        >
          ⚑ Cheat
        </span>
      )}
      {run.flaggedAt && (
        <span
          title={[run.flaggedReason, run.flaggedByEmail ? `by ${run.flaggedByEmail}` : null]
            .filter(Boolean)
            .join(' — ') || undefined}
          className="rounded bg-[#3a2a0e] px-2 py-0.5 text-[11px] text-[#ffd17a]"
        >
          Flagged
        </span>
      )}
      {run.cheatSignals.telemetryMissing && (
        <span className="rounded bg-white/5 px-2 py-0.5 text-[11px] text-white/45">no telemetry</span>
      )}
      {run.cheatSignals.telemetryError && (
        <span className="rounded bg-white/5 px-2 py-0.5 text-[11px] text-white/45">telemetry error</span>
      )}
      {!run.suspicious && !run.flaggedAt && !run.cheatSignals.telemetryMissing && !run.cheatSignals.telemetryError && (
        <span className="text-[11px] text-white/35">—</span>
      )}
    </div>
  );
}

function RunDetail({ run }: { run: UserRun }) {
  const [showRaw, setShowRaw] = useState(false);
  const t = run.telemetry;
  const active = activeSignalLabels(run.cheatSignals);

  return (
    <div className="flex flex-col gap-3 rounded-md border border-white/10 bg-black/20 p-3">
      {run.flaggedAt && (
        <div className="rounded border border-[#c0a94f]/30 bg-[#3a2a0e]/40 p-2 text-xs text-[#ffd17a]">
          <strong className="font-semibold">Flagged</strong>
          {run.flaggedReason ? `: ${run.flaggedReason}` : ''}
          {run.flaggedByEmail || run.flaggedAt ? (
            <span className="text-white/55">
              {' '}
              ({run.flaggedByEmail ? `by ${run.flaggedByEmail}` : 'by unknown'}
              {run.flaggedAt ? ` on ${fmtDateTime(run.flaggedAt)}` : ''})
            </span>
          ) : null}
        </div>
      )}

      {active.length > 0 && (
        <div className="text-xs text-[#ff8da6]">
          <span className="uppercase tracking-wide text-white/45">Signals: </span>
          {active.join(' · ')}
        </div>
      )}

      {t == null ? (
        <p className="text-xs text-white/50">No telemetry recorded (legacy run or older game build).</p>
      ) : (
        <dl className="grid gap-x-6 gap-y-1.5 text-xs sm:grid-cols-2 lg:grid-cols-3">
          <MiniField label="Real duration" value={fmtDuration(t.realDurationSec)} />
          <MiniField label="Avg FPS" value={t.fpsAvg != null ? String(t.fpsAvg) : '—'} />
          <MiniField label="Reached boss" value={yesNo(t.reachedBoss)} />
          <MiniField label="Boss defeated" value={yesNo(t.bossDefeated)} />
          <MiniField label="Entered swarm" value={yesNo(t.enteredSwarm)} />
          <MiniField label="Swarm duration" value={fmtDuration(t.swarmDurationSec)} />
          <MiniField label="Times hurt" value={t.timesHurt != null ? fmtNum(t.timesHurt) : '—'} />
          <MiniField label="Damage instances" value={t.damageInstances != null ? fmtNum(t.damageInstances) : '—'} />
          <MiniField label="Min / max life" value={`${t.minLife ?? '—'} / ${t.maxLife ?? '—'}`} />
          <MiniField label="Master speed" value={t.masterSpeed != null ? String(t.masterSpeed) : '—'} />
          <MiniField
            label="Cheat ever"
            value={t.cheatEver ? `Yes${t.cheatEverAtSec != null ? ` (at ${t.cheatEverAtSec}s)` : ''}` : 'No'}
            danger={t.cheatEver === true}
          />
          <MiniField
            label="Cheat at end"
            value={cheatEndSnapshot(t.cheat)}
            danger={!!(t.cheat && (t.cheat.inv || t.cheat.invPerm || t.cheat.fast || t.cheat.enemyPaused))}
          />
        </dl>
      )}

      <div>
        <button
          type="button"
          onClick={() => setShowRaw((v) => !v)}
          className="text-xs text-white/60 underline transition hover:text-white/85"
        >
          {showRaw ? 'Hide raw payload' : 'Show raw payload'}
        </button>
        {showRaw && (
          <pre className="mt-2 max-h-80 overflow-auto rounded border border-white/10 bg-black/40 p-3 text-[11px] leading-relaxed text-white/70">
            {safeStringify(run.payload)}
          </pre>
        )}
      </div>

      <p className="text-[11px] text-white/35">
        Record #{run.recordId} · seq {run.monotonicSeq}
        {run.sessionId ? ` · session ${run.sessionId}` : ' · session swept'}
        {run.ipAddress ? ` · ${run.ipAddress}` : ''}
      </p>
    </div>
  );
}

function FlagRunModal({
  userId,
  run,
  onClose,
  onDone,
}: {
  userId: string;
  run: UserRun;
  onClose: () => void;
  onDone: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    ref.current?.focus();
  }, []);

  const ok = reason.trim().length >= 3;

  const confirm = async (): Promise<void> => {
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await flagUserRun(userId, run.recordId, reason.trim());
      onDone(reason.trim());
    } catch (err) {
      setBusy(false);
      setError(err instanceof ApiError ? err.message : 'Could not flag that run. Please try again.');
    }
  };

  return (
    <Modal title="Flag run for review" onClose={onClose} disabled={busy}>
      <p className="mt-3 text-sm text-white/80">
        Flag Week {run.weekNumber} run (score{' '}
        <span className="text-white">{fmtNum(run.score)}</span>) as cheating / fishy. This is an
        internal marker recorded in the audit log — it does not remove the player from the
        leaderboard.
      </p>
      <label className="mt-4 flex flex-col gap-1 text-xs uppercase tracking-wide text-white/55">
        Reason
        <textarea
          ref={ref as RefObject<HTMLTextAreaElement>}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          maxLength={255}
          className="rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm normal-case tracking-normal text-white placeholder:text-white/35 focus:border-white/40 focus:outline-none"
          placeholder="Recorded in the audit log"
        />
      </label>
      {error && <p className="mt-3 text-sm text-[#ff8da6]">{error}</p>}
      <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="rounded-full border border-white/25 px-5 py-2 text-sm text-white transition hover:bg-white/10 disabled:opacity-50 sm:min-w-[120px]"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void confirm()}
          disabled={busy || !ok}
          className="rounded-full bg-[#c0344f] px-5 py-2 text-sm font-medium text-white transition hover:bg-[#d83c5a] disabled:cursor-not-allowed disabled:opacity-50 sm:min-w-[160px]"
        >
          {busy ? 'Working…' : 'Flag run'}
        </button>
      </div>
    </Modal>
  );
}

function WeekChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={[
        'rounded-md border px-3 py-1 text-xs transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white',
        active
          ? 'border-brand-redAccent bg-brand-redAccent/20 text-white'
          : 'border-white/15 text-white/70 hover:bg-white/10',
      ].join(' ')}
    >
      {label}
    </button>
  );
}

function MiniField({ label, value, danger }: { label: string; value: string; danger?: boolean }) {
  return (
    <div>
      <dt className="uppercase tracking-wide text-white/40">{label}</dt>
      <dd className={danger ? 'font-medium text-[#ff8da6]' : 'text-white/85'}>{value}</dd>
    </div>
  );
}

function activeSignalLabels(s: UserRunCheatSignals): string[] {
  const out: string[] = [];
  if (s.invincible) out.push('Invincible');
  if (s.permanentInvincible) out.push('Permanent invincible');
  if (s.fastMove) out.push('Fast move');
  if (s.enemyPaused) out.push('Enemy paused');
  if (s.noDamage) out.push('No damage taken');
  return out;
}

function cheatEndSnapshot(
  cheat: { inv?: boolean; invPerm?: boolean; fast?: boolean; enemyPaused?: boolean } | undefined,
): string {
  if (!cheat) return '—';
  const on: string[] = [];
  if (cheat.inv) on.push('inv');
  if (cheat.invPerm) on.push('inv-perm');
  if (cheat.fast) on.push('fast');
  if (cheat.enemyPaused) on.push('enemy-paused');
  return on.length ? on.join(', ') : 'clean';
}

function yesNo(v: boolean | undefined): string {
  return v ? 'Yes' : 'No';
}

function fmtDuration(sec: number | null | undefined): string {
  if (sec == null) return '—';
  const s = Math.max(0, Math.round(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}m ${r}s` : `${r}s`;
}

function safeStringify(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

function ConsentsCard({ detail }: { detail: UserDetail }) {
  return (
    <Card title={`Rules consents (${detail.consents.length})`}>
      {detail.consents.length === 0 ? (
        <p className="text-sm text-white/50">No consents on record.</p>
      ) : (
        <ul className="flex flex-col gap-1.5 text-sm">
          {detail.consents.map((c) => (
            <li key={`${c.weekNumber}-${c.rulesVersion}`} className="flex items-baseline justify-between gap-3">
              <span className="text-white/85">
                Week {c.weekNumber} v{c.rulesVersion}
              </span>
              <span className="text-xs text-white/55">
                {c.source} · {fmtDate(c.agreedAt)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function AuditTailCard({ detail }: { detail: UserDetail }) {
  return (
    <Card title={`Recent activity (${detail.auditTail.length})`}>
      {detail.auditTail.length === 0 ? (
        <p className="text-sm text-white/50">No recorded activity.</p>
      ) : (
        <ul className="flex flex-col gap-2 text-sm">
          {detail.auditTail.map((a) => (
            <li key={a.logId} className="border-b border-white/5 pb-2 last:border-0">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-mono text-xs text-white/80">{a.action}</span>
                <span className="text-xs text-white/45">{fmtDateTime(a.createdAt)}</span>
              </div>
              {a.actorEmail && <div className="text-xs text-white/55">by {a.actorEmail}</div>}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ── primitives ─────────────────────────────────────────────────────────

function Card({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <section className="rounded-xl border border-white/15 bg-black/20 p-5">
      {title && <h3 className="mb-3 font-heading text-xs uppercase tracking-wide text-white/55">{title}</h3>}
      {children}
    </section>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-white/45">{label}</dt>
      <dd className="text-white/90 [overflow-wrap:anywhere]">{value}</dd>
    </div>
  );
}

function Th({ children }: { children: ReactNode }) {
  return <th className="px-3 py-2 font-heading text-xs uppercase tracking-wide">{children}</th>;
}

function StatusBadge({ status }: { status: UserStatusValue }) {
  const map: Record<UserStatusValue, string> = {
    ACTIVE: 'bg-[#0f3d2a] text-[#5bffb4]',
    PENDING: 'bg-[#3d360f] text-[#ffd95b]',
    SUSPENDED: 'bg-[#3d240f] text-[#ffae5b]',
  };
  return <span className={`rounded px-2 py-0.5 text-xs ${map[status]}`}>{status}</span>;
}

function TypeBadge({ type }: { type: UserTypeValue }) {
  const map: Record<UserTypeValue, string> = {
    USER: 'bg-white/10 text-white/70',
    ADMIN: 'bg-[#2a1140] text-[#d08dff]',
    DEV_USER: 'bg-white/5 text-white/45',
  };
  const label = type === 'DEV_USER' ? 'DEV' : type;
  return <span className={`rounded px-2 py-0.5 text-xs ${map[type]}`}>{label}</span>;
}

function PillBadge({
  children,
  tone,
  title,
}: {
  children: ReactNode;
  tone: 'danger' | 'warn';
  title?: string;
}) {
  const cls =
    tone === 'danger' ? 'bg-[#3a0e1a] text-[#ff8da6]' : 'bg-[#3a2a0e] text-[#ffd17a]';
  return (
    <span title={title} className={`rounded px-2 py-0.5 text-xs ${cls}`}>
      {children}
    </span>
  );
}

